import { useCallback, useEffect, useRef, useState } from 'react';

export interface HoverOpenHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export interface UseHoverOpenResult {
  close: () => void;
  /** Hover handlers for the floating content: entering it cancels a pending close. */
  floatingHoverProps: HoverOpenHandlers;
  open: boolean;
  /** Opens without delay; used by click, touch and keyboard activation. */
  openByClick: () => void;
  /** True while the content was opened by the pointer only (no click/keyboard). */
  openedByHover: boolean;
  /** Hover handlers for the trigger: entering it opens the content. */
  referenceHoverProps: HoverOpenHandlers;
}

/**
 * Open state of an interactive hover card: opens when the pointer enters the trigger and closes
 * `closeDelayMs` after it leaves both the trigger and the content, so the pointer can travel
 * from one to the other without the card closing.
 */
export function useHoverOpen(closeDelayMs: number): UseHoverOpenResult {
  const [open, setOpen] = useState(false);
  const [openedByHover, setOpenedByHover] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelClose = useCallback(() => clearTimeout(closeTimer.current), []);

  const close = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(close, closeDelayMs);
  }, [cancelClose, close, closeDelayMs]);

  const openByHover = useCallback(() => {
    cancelClose();
    if (!open) {
      setOpenedByHover(true);
      setOpen(true);
    }
  }, [cancelClose, open]);

  const openByClick = useCallback(() => {
    cancelClose();
    setOpenedByHover(false);
    setOpen(true);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  return {
    close,
    floatingHoverProps: { onMouseEnter: cancelClose, onMouseLeave: scheduleClose },
    open,
    openByClick,
    openedByHover,
    referenceHoverProps: { onMouseEnter: openByHover, onMouseLeave: scheduleClose }
  };
}
